import {
  HeadContent,
  Outlet,
  createRootRouteWithContext,
  redirect,
  useRouterState,
} from "@tanstack/react-router";
import { TanStackRouterDevtools } from "@tanstack/react-router-devtools";
import { useEffect } from "react";

import TanstackQueryLayout from "../integrations/tanstack-query/layout";
import type { QueryClient } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/sonner";
import { saveUtmParams, utmSchema } from "@/lib/utils/utm";

interface MyRouterContext {
  queryClient: QueryClient;
  isProblematicScrollPage?: boolean;
}

const rootSearchSchema = utmSchema;

export const Route = createRootRouteWithContext<MyRouterContext>()({
  validateSearch: rootSearchSchema,
  beforeLoad({ search }) {
    if (search.utm_source || search.utm_medium || search.utm_campaign) {
      saveUtmParams(search);
      throw redirect({
        to: ".",
        search: (s) => {
          delete s.utm_source;
          delete s.utm_medium;
          delete s.utm_campaign;
          return s;
        },
      });
    }
  },
  component: () => {
    const problematicScroll = useRouterState({
      select: (s) => s.matches.some((m) => m.context.isProblematicScrollPage),
    });

    useEffect(() => {
      if (problematicScroll) document.body.dataset.problematicScroll = "";
      else delete document.body.dataset.problematicScroll;

      return () => void delete document.body.dataset.problematicScroll;
    }, [problematicScroll]);

    return (
      <>
        <HeadContent />
        <Outlet />
        <TanStackRouterDevtools />
        <Toaster />
        <TanstackQueryLayout />
      </>
    );
  },
  head({ matches }) {
    const foundMatch = matches.findLast((m) => m.staticData.title);
    let title = "Polygraf AI Dashboard";
    if (foundMatch?.staticData.title) {
      title = `${foundMatch.staticData.title} | ` + title;
    }
    return {
      meta: [
        {
          title,
        },
        {
          name: "description",
          content:
            "Polygraf AI Dashboard gives you instant access to your tools, usage stats, and account activity. Monitor token usage, launch actions fast, and stay in control of your AI experience.",
        },
      ],
    };
  },
});
